import type { AgenticLogicRuleDto } from "@intelligems/ig-types";
import { IgCodeEditor, IgTypography } from "@intelligems/ig-theme";
import { Stack } from "@mui/material";
import { useAgenticLogicRuleDraft } from "@src/api/agentic-logic/get-agentic-logic-rule-draft";
import { SelectionLogicTab } from "../graph/SelectionLogicTab";
import { useTemplateSelectionGraph } from "../graph/useTemplateSelectionGraph";

/** Reads only saved inputs. Local editor buffers never enter version history. */
export const SavedRuleDraft = ({
  rule,
  advancedEnabled,
}: {
  rule: AgenticLogicRuleDto;
  advancedEnabled: boolean;
}) => {
  const query = useAgenticLogicRuleDraft(
    rule.managedBy === "code" ? rule.id : undefined
  );
  const draft = query.data?.draft;
  const template = useTemplateSelectionGraph(rule);
  return (
    <Stack spacing={2}>
      <IgTypography variant="body2">
        Saved draft. Publishing this draft creates a new published version.
      </IgTypography>
      <Stack sx={{ height: 420 }}>
        {rule.managedBy === "builder" ? (
          <SelectionLogicTab
            graph={template.graph}
            isLoading={template.isLoading}
          />
        ) : (
          <SelectionLogicTab
            graph={draft?.graph ?? null}
            isLoading={query.isLoading}
            emptyHint="The saved draft's graph is not available yet."
          />
        )}
      </Stack>
      <IgTypography variant="body2">
        Up to {rule.settings.maxItems} products. Out-of-stock products:{" "}
        {rule.settings.excludeOutOfStock ? "excluded" : "included"}. Products in
        the cart: {rule.settings.excludeItemsInCart ? "excluded" : "included"}.
        If no products match:{" "}
        {rule.settings.onEmpty === "hide"
          ? "hide recommendations"
          : "show smart recommendations"}
        .
      </IgTypography>
      {advancedEnabled ? (
        <IgCodeEditor
          aria-label="Saved draft source"
          language={rule.managedBy === "code" ? "ts" : "json"}
          value={
            rule.managedBy === "code"
              ? (draft?.source ?? "")
              : JSON.stringify(rule.templateParams, null, 2)
          }
          readOnly
          copy
        />
      ) : null}
    </Stack>
  );
};
